import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';
import {
  BaseAssignmentType,
  BaseFilterService,
  PspBranchFilterInfoResponse,
  QualificationShortInfoResponse,
  BaseQueryParamService,
  CustomerDepartmentFilterInfoResponse,
  CustomerLocationFilterInfoResponse,
} from 'base';
import { BaseCustomerEmployeeContractSignStatusesEnum } from '../../_enums/base-customer-external-employee-contract-sign-statuses.enum';
import { BaseCustomerEmployeesFiltersEnum } from '../../_enums/base-customer-external-employee-filters.enum';
import {
  BaseCustomerExternalEmployeeFilterFormInterface,
  FilterFormExternalEmployees,
} from '../../_interfaces/base-customer-external-employee-filter-form.interface';

@Component({
  selector: 'base-customer-external-employees-filters',
  templateUrl: './base-customer-external-employee-filters.component.html',
})
export class BaseCustomerExternalEmployeeFiltersComponent implements OnInit, OnDestroy {
  @Input() branches: PspBranchFilterInfoResponse[] = [];
  @Input() qualifications: QualificationShortInfoResponse[] = [];
  @Input() departments: CustomerDepartmentFilterInfoResponse[] = [];
  @Input() locations: CustomerLocationFilterInfoResponse[] = [];
  @Input() filterValues: { [key in any]: any };
  @Input() filtersForm: FormGroup<BaseCustomerExternalEmployeeFilterFormInterface>;
  @Input({ required: true }) isWorkCouncil: boolean;
  @Output() filtersChanged: EventEmitter<any> = new EventEmitter<any>();
  activeFilters: any[] = [];
  baseCustomerInquiryFilters = BaseCustomerEmployeesFiltersEnum;
  statusItems: { name: string; value: BaseAssignmentType; class: string }[] = [
    { name: 'planned', value: BaseAssignmentType.PLANNED, class: 'planned-operation-state' },
    { name: 'states.active', value: BaseAssignmentType.ACTIVE, class: 'active-operation-state' },
    { name: 'completed', value: BaseAssignmentType.ENDED, class: 'ended-operation-state' },
  ];
  openContractItems: { name: string; value: BaseCustomerEmployeeContractSignStatusesEnum }[] = [
    {
      name: 'candidates.status.signed',
      value: BaseCustomerEmployeeContractSignStatusesEnum.SIGNED,
    },
    {
      name: 'externalEmployees.contractStatus.signatureRequired',
      value: BaseCustomerEmployeeContractSignStatusesEnum.SIGNATURE_REQUIRED,
    },
    {
      name: 'externalEmployees.contractStatus.waitingForPartner',
      value: BaseCustomerEmployeeContractSignStatusesEnum.WAITING_FOR_PARTNER,
    },
  ];
  labels: any = {
    status: 'state',
    mainQualification: 'qualifications.plural',
    openContract: 'contract',
    fromDate: 'inUseFrom',
    pspBranch: 'provider',
    search: 'search',
    department: 'department.singular',
    location: 'location.singular',
  };

  private destroy$ = new Subject();

  constructor(public filterService: BaseFilterService, private queryParamsService: BaseQueryParamService) {}

  ngOnInit(): void {
    this.initFilterValuesByQueryParams();
    this.subscribeFiltersFormChanges();
  }

  ngOnDestroy() {
    this.destroy$.next(true);
  }

  removeFilter(filter: FilterFormExternalEmployees): void {
    this.filterService.removeFilter(this.filtersForm, filter);
  }

  clearFilters(): void {
    this.filtersForm.reset();
  }

  private initFilterValuesByQueryParams() {
    this.filterValues = this.filterService.setInitialFilterValuesByQueryParams(
      this.filterValues,
      {
        mainQualification: this.filterService.convertArrayToKeyValueObject(this.qualifications, 'id', 'name'),
        pspBranch: this.filterService.convertArrayToKeyValueObject(this.branches, 'id', 'name'),
        department: this.filterService.convertArrayToKeyValueObject(this.departments, 'id', 'name'),
        location: this.filterService.convertArrayToKeyValueObject(this.locations, 'id', 'name'),
        fromDate: { active: 'states.active' },
        status: { active: 'states.active' },
        openContract: {
          signed: 'candidates.status.signed',
          customer: 'externalEmployees.contractStatus.signatureRequired',
          psp: 'externalEmployees.contractStatus.waitingForPartner',
        },
      },
      ['fromDate']
    );

    this.filtersForm.patchValue(this.filterValues);
    this.activeFilters = this.filterService.updateActiveFilters(this.filtersForm.getRawValue());
  }

  private subscribeFiltersFormChanges() {
    this.filtersForm.valueChanges.pipe(takeUntil(this.destroy$), debounceTime(50)).subscribe(() => {
      this.activeFilters = this.filterService.updateActiveFilters(this.filtersForm.getRawValue());
      this.queryParamsService.filterSubject.next({
        filters: this.filtersForm.value,
        page: 1,
      });
    });
  }
}
